import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatIsoDay, formatMoney, formatNumber, formatPct, formatTRY } from "@/lib/format";
import { PAYMENTS, PRINT_DOCS, STATUS, printSummary } from "@/lib/orders";
import InvoiceDoc from "@/components/print/InvoiceDoc";
import InvoiceShare from "@/components/orders/InvoiceShare";
import { BRAND } from "@/lib/brand";
import TemplatePrint from "@/pages/TemplatePrint";

const Head = ({ o, title, brand }) => (
  <div className="flex justify-between items-start border-b-2 border-slate-900 pb-3 mb-4">
    <div>
      <div className="text-xl font-bold tracking-tight" data-testid="print-brand">{brand || "İMAMOĞLU A.Ş"}</div>
      <div className="text-[11px] text-slate-500"></div>
    </div>
    <div className="text-right">
      <div className="text-lg font-bold uppercase" data-testid="print-doc-title">{title}</div>
      <div className="font-mono text-sm">{o.order_no}</div>
      <div className="text-[11px] text-slate-500">Sipariş tarihi {formatIsoDay(o.order_date)} · Durum {STATUS[o.status]?.[0]}</div>
    </div>
  </div>
);

const Sign = ({ labels }) => (
  <div className={`grid ${labels.length === 3 ? "grid-cols-3" : "grid-cols-2"} gap-8 mt-12 text-[12px]`}>
    {labels.map((l) => <div key={l} className="border-t border-slate-400 pt-1 text-center text-slate-600">{l}</div>)}
  </div>
);

const OrderForm = ({ o, title }) => (
  <>
    <Head o={o} title={title} />
    <div className="grid grid-cols-2 gap-6 text-[12px] mb-4">
      <div><div className="text-slate-500">Cari</div><div className="font-semibold">{o.customer_code} · {o.customer_title}</div></div>
      <div className="text-right"><div className="text-slate-500">Temsilci / Ödeme</div><div>{o.sales_rep_name} · {PAYMENTS[o.payment_method]} · Vade {formatIsoDay(o.due_date)}</div></div>
    </div>
    <table className="w-full text-[12px] border-collapse">
      <thead><tr className="border-b border-slate-900 text-left"><th className="py-1">#</th><th>SKU</th><th>Ürün</th><th className="text-right">Koli</th><th className="text-right">Adet</th><th className="text-right">Birim</th><th className="text-right">İsk.</th><th className="text-right">Toplam</th></tr></thead>
      <tbody>{o.lines.map((l, i) => (
        <tr key={l.sku} className="border-b border-slate-200"><td className="py-1">{i + 1}</td><td className="font-mono">{l.sku}</td><td>{l.name}</td>
          <td className="text-right font-mono">{formatNumber(l.koli, 2)}</td><td className="text-right font-mono">{formatNumber(l.qty)}</td>
          <td className="text-right font-mono">{formatMoney(l.unit_price, o.currency)}</td><td className="text-right font-mono">{formatPct(l.discount_pct)}</td>
          <td className="text-right font-mono">{formatMoney(l.line_net, o.currency)}</td></tr>))}</tbody>
    </table>
    <div className="ml-auto w-72 mt-3 text-[12px] space-y-0.5">
      {[["Ara Toplam", formatMoney(o.subtotal, o.currency)], ["İskonto", formatMoney(-o.discount_total, o.currency)], ["KDV", formatMoney(o.vat_total, o.currency)],
        ["Genel Toplam", formatMoney(o.grand_total, o.currency)], ...(o.currency !== "TRY" ? [["TL Karşılığı", formatTRY(o.grand_total_try)]] : [])].map(([k, v]) => (
        <div key={k} className={`flex justify-between ${k === "Genel Toplam" ? "font-bold border-t border-slate-900 pt-1" : ""}`}><span>{k}</span><span className="font-mono">{v}</span></div>))}
    </div>
    {o.note && <div className="mt-3 text-[12px]"><b>Not:</b> {o.note}</div>}
    <Sign labels={["Hazırlayan", "Müşteri Onayı"]} />
  </>
);

const ProformaDoc = ({ o }) => (
  <div data-testid="proforma-doc">
    <Head o={o} title="PROFORMA" brand={BRAND} />
    <div className="text-[12px] mb-4"><div className="text-slate-500">Cari</div><div className="font-semibold">{o.customer_code} · {o.customer_title}</div></div>
    <table className="w-full text-[12px] border-collapse" data-testid="proforma-lines">
      <thead><tr className="border-b border-slate-900 text-left"><th className="py-1 w-8">#</th><th>Ürün Kodu</th><th>Ürün</th><th className="text-right">Miktar</th><th>Birim</th></tr></thead>
      <tbody>{o.lines.map((l, i) => (
        <tr key={`${l.sku}-${i}`} className="border-b border-slate-200"><td className="py-1">{i + 1}</td><td className="font-mono">{l.sku}</td><td>{l.name}</td>
          <td className="text-right font-mono">{formatNumber(l.qty)}</td><td className="pl-2">{l.unit || "Adet"}</td></tr>))}</tbody>
    </table>
    {o.note && <div className="mt-3 text-[12px]"><b>Not:</b> {o.note}</div>}
    <Sign labels={["Hazırlayan", "Müşteri Onayı"]} />
  </div>
);

const PickList = ({ o }) => {
  const lines = [...o.lines].sort((a, b) => (a.raf || "").localeCompare(b.raf || ""));
  return (
    <>
      <Head o={o} title="Depo Toplama Listesi" />
      <div className="text-[12px] mb-3"><b>{o.customer_title}</b> · {lines.length} kalem · {formatNumber(lines.reduce((s, l) => s + l.qty, 0))} adet · {formatNumber(lines.reduce((s, l) => s + l.koli, 0), 2)} koli</div>
      <table className="w-full text-[13px] border-collapse">
        <thead><tr className="border-b border-slate-900 text-left"><th className="py-1 w-6">✓</th><th>Raf</th><th>Depo</th><th>SKU / Barkod</th><th>Ürün</th><th className="text-right">Koli</th><th className="text-right">Adet</th><th className="text-right w-24">Toplanan</th></tr></thead>
        <tbody>{lines.map((l) => (
          <tr key={l.sku} className="border-b border-slate-300"><td className="py-2"><span className="inline-block h-4 w-4 border border-slate-500" /></td>
            <td className="font-mono font-bold">{l.raf || "—"}</td><td>{l.warehouse_name}</td>
            <td className="font-mono text-[11px]">{l.sku}<br />{l.barcode}</td><td>{l.name}{l.note && <div className="text-[11px] italic">Açıklama: {l.note}</div>}</td>
            <td className="text-right font-mono">{formatNumber(l.koli, 2)}</td><td className="text-right font-mono font-bold">{formatNumber(l.qty)}</td><td className="border-b border-slate-400" /></tr>))}</tbody>
      </table>
      {o.note && <div className="mt-3 text-[12px] whitespace-pre-wrap" data-testid="picklist-order-note"><b>Sipariş Açıklaması:</b> {o.note}</div>}
      <Sign labels={["Toplayan", "Kontrol Eden", "Paketleyen"]} />
    </>
  );
};

const recorded = new Set(); // StrictMode/yeniden render'a karşı: konum anahtarı başına tek baskı kaydı

export default function OrderPrint() {
  const { doc } = useParams();
  if (doc === "fatura" || doc === "proforma") return <TemplatePrint tidOverride={`default:${doc}`} />;  // eski bağlantılar → varsayılan şablon
  return <LegacyPrint />;
}

function LegacyPrint() {
  const { id, doc } = useParams();
  const navigate = useNavigate();
  const loc = useLocation();
  const [sp, setSp] = useSearchParams();
  const started = useRef(false);
  const [o, setO] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => setO(data)).catch(() => setErr("Sipariş yüklenemedi"));
    api.get(`/orders/${id}/invoice-meta`).then(({ data }) => setMeta(data)).catch(() => setMeta({ company: null, customer: {} }));
  }, [id]);
  useEffect(() => {
    if (!o || !meta || started.current) return;
    started.current = true;
    const run = async () => {
      if (sp.get("kayit") === "1" && doc === "fatura" && o.status === "faturalasti" && !recorded.has(loc.key)) {
        recorded.add(loc.key);
        try { const { data } = await api.post(`/orders/${id}/print`, { doc }); setO((p) => ({ ...p, ...data })); } catch { /* baskı yine açılır */ }
        setSp({}, { replace: true });
      }
      await document.fonts.ready;
      setTimeout(() => window.print(), 300);
    };
    run();
  }, [o, meta]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="p-8 text-rose-600">{err}</div>;
  if (!o || !meta) return <div className="p-8 text-slate-400">Yükleniyor…</div>;
  if (doc === "fatura" && o.status !== "faturalasti") return <div className="p-8 text-rose-600" data-testid="invoice-not-ready">Satış faturası yalnızca FATURALAŞTI siparişler için yazdırılabilir</div>;
  return (
    <div className="bg-slate-100 min-h-screen print:bg-white" data-testid="order-print-page">
      <div className="max-w-[260mm] mx-auto py-4 px-2 print:hidden flex flex-wrap gap-2 justify-between items-center">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/siparisler/${id}`)} className="h-9 px-3 rounded border border-slate-300 bg-white text-sm flex items-center gap-1 whitespace-nowrap hover:bg-slate-50" data-testid="print-back-to-order-button"><ArrowLeft className="h-4 w-4" />Siparişe Dön</button>
          <span className="text-sm text-slate-600">{PRINT_DOCS[doc]} · {o.order_no}</span>
        </div>
        <div className="flex items-center gap-2">
        {doc === "fatura" && <InvoiceShare id={id} />}
        <button onClick={() => window.print()} className="h-9 px-3 rounded bg-slate-900 text-white text-sm flex items-center gap-1 whitespace-nowrap" data-testid="print-again-button"><Printer className="h-4 w-4" />Yazdır / PDF</button>
        </div>
      </div>
      <div className="max-w-[210mm] mx-auto bg-white p-[12mm] shadow print:shadow-none print:p-0 text-slate-900">
        {doc === "fatura" ? <InvoiceDoc o={o} meta={meta} /> : doc === "toplama_listesi" ? <PickList o={o} /> : doc === "proforma" ? <ProformaDoc o={o} /> : <OrderForm o={o} title="Sipariş Formu" />}
        {doc !== "proforma" && <div className="mt-8 text-[10px] text-slate-400" data-testid="print-footer">Yazdırma: {formatDate(new Date().toISOString())} · Baskı sayısı: {printSummary(o.print_counts)}</div>}
      </div>
    </div>
  );
}
